import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, Tabs, Table, Tag, Button, Space, Spin, Descriptions, App, Alert } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { dataSourcesApi, unwrapKingdeeTabRows, unwrapKingdeeTabAttachments } from '../../services/api'
import { formatDisplayDateTime, formatOnChainLabel } from '../../utils/format'
import type { SupplementTab } from './SupplementMaterialsModal'
import RelationRowDetailModal from './RelationRowDetailModal'
import OutstockDetailModal from './OutstockDetailModal'
import { AttachmentLinksCell } from './relationAttachments'
import { formatDocumentStatus } from '../../utils/documentStatus'

const statusTextMap: Record<string, string> = {
  available: '可用',
  void: '作废',
  written_off: '已核销',
  used: '已使用',
}

const statusColorMap: Record<string, string> = {
  available: 'green',
  void: 'red',
  written_off: 'orange',
  used: 'blue',
}

/** 与资产数据列表字段一致 */
type DetailRecord = {
  id: string
  name: string
  source: string
  type: string
  /** 上链标记：常见为「是 / 否」，概要「单据状态」优先据此展示 */
  onChain?: string
  documentStatus: string
  documentStatusName: string
  contractNo: string
  contractType: string
  customer: string
  settlementOrg: string
  productName: string
  priceTaxTotal: number
  receiptAmount: number
  invoicedAmount: number
  contractCreatedDate: string
  contractReceiptDate: string
  value: number
  status: string
  createdTime: string
  /** 与金蝶 Tab / 合同上链接口一致：销售订单号 */
  ordernumber: string
  /** 后端若有摘要/说明可展示在概要末尾 */
  summary?: string
  updateTime?: string
}

/** 与 pickDetail 使用同一层 payload（支持 `{ data: {...} }` 或扁平对象） */
function getDetailDataPayload(res: unknown): Record<string, unknown> | null {
  if (!res || typeof res !== 'object') return null
  const raw = res as Record<string, unknown>
  const d = raw.data && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : raw
  return d
}

/** 金蝶五个 Tab 所需：从 detail 接口 data 中解析（字段名兼容多种后端写法） */
function pickKingdeeParamsFromDetail(d: Record<string, unknown>): {
  ordernumber: string
  bill_no: string
  /** 发票接口 `bill_no` 常用应收单号（如 AR00003922）；缺省则与 `bill_no` 相同 */
  invoiceBillNo: string
} {
  const ordernumber = String(
    d.ordernumber ??
      d.orderNumber ??
      d.order_number ??
      d.orderNo ??
      d.order_no ??
      d.FOrderNo ??
      d.forder_no ??
      '',
  ).trim()
  const bill_no = String(
    d.bill_no ??
      d.billNo ??
      d.BillNo ??
      d.documentNo ??
      d.document_no ??
      d.billNumber ??
      d.bill_number ??
      d.billno ??
      '',
  ).trim()
  const invoiceBillNo = String(
    d.invoiceBillNo ??
      d.invoice_bill_no ??
      d.arBillNo ??
      d.ar_bill_no ??
      d.receivableBillNo ??
      d.receivable_bill_no ??
      d.arDocumentNo ??
      d.ar_document_no ??
      d.arNo ??
      d.ar_no ??
      d.receivableNo ??
      d.receivable_no ??
      '',
  ).trim()
  const contractNoRaw = String(d.contractNo ?? d.contract_no ?? '').trim()
  const invoiceBillNoResolved =
    invoiceBillNo || (/^AR/i.test(contractNoRaw) ? contractNoRaw : '')
  return { ordernumber, bill_no, invoiceBillNo: invoiceBillNoResolved }
}

function pickDetail(raw: any, id: string): DetailRecord | null {
  const d = getDetailDataPayload(raw)
  if (!d) return null
  return {
    id: String(d.id ?? d.data_id ?? id),
    name: String(d.name ?? d.data_name ?? ''),
    source: String(d.source ?? ''),
    type: String(d.type ?? '应收'),
    onChain: String(d.onChain ?? d.on_chain ?? '').trim() || undefined,
    documentStatus:
      String(d.documentStatus ?? d.document_status ?? d.billStatus ?? d.bill_status ?? '') || '',
    documentStatusName:
      String(d.documentStatusName ?? d.document_status_name ?? '') || '',
    contractNo: String(d.contractNo ?? d.contract_no ?? d.contractNumber ?? '') || '',
    contractType: String(d.contractType ?? d.contract_type ?? '') || '',
    customer: String(d.customer ?? d.customerName ?? '') || '',
    settlementOrg: String(d.settlementOrg ?? d.settlement_org ?? d.orgName ?? '') || '',
    productName: String(d.productName ?? d.product_name ?? d.goodsName ?? d.goods_name ?? '') || '',
    priceTaxTotal: Number(d.priceTaxTotal ?? d.price_tax_total ?? d.totalAmount ?? d.amount ?? 0),
    receiptAmount: Number(d.receiptAmount ?? d.receipt_amount ?? d.receivedAmount ?? 0),
    invoicedAmount: Number(d.invoicedAmount ?? d.invoiced_amount ?? d.invoiceAmount ?? 0),
    contractCreatedDate:
      String(
        d.contractCreatedDate ??
          d.contract_created_date ??
          d.contractCreatedAt ??
          d.contract_created_at ??
          ''
      ) || '',
    contractReceiptDate:
      String(d.contractReceiptDate ?? d.contract_receipt_date ?? d.receiptDate ?? d.receipt_date ?? '') || '',
    value: Number(d.value ?? d.amount ?? 0),
    status: String(d.status ?? d.assetStatus ?? 'available'),
    createdTime:
      String(
        d.createdTime ?? d.created_at ?? d.createdDate ?? d.create_time ?? d.created_date ?? ''
      ) || '',
    ordernumber: String(
      d.ordernumber ??
        d.orderNumber ??
        d.order_number ??
        d.orderNo ??
        d.order_no ??
        d.FOrderNo ??
        d.forder_no ??
        '',
    ).trim(),
    summary: (() => {
      const s = d.summary ?? d.description
      if (s == null) return undefined
      const t = String(s).trim()
      return t ? t : undefined
    })(),
    updateTime: (() => {
      const u = d.updateTime ?? d.updated_at ?? d.update_time
      return u != null && String(u).trim() !== '' ? String(u) : undefined
    })(),
  }
}

function fmtMoney(n: number) {
  return `¥${Number(n ?? 0).toLocaleString()}`
}

/** 合同行：兼容后端多种字段名 */
function contractPartyA(row: Record<string, unknown>) {
  const v = row.partyA ?? row.party_a ?? row.firstParty ?? row.first_party ?? row.jiafang ?? row.partyAName
  return v != null && String(v).trim() !== '' ? String(v) : '-'
}
function contractPartyB(row: Record<string, unknown>) {
  const v = row.partyB ?? row.party_b ?? row.secondParty ?? row.second_party ?? row.yifang ?? row.partyBName
  return v != null && String(v).trim() !== '' ? String(v) : '-'
}
function contractPeriodText(row: Record<string, unknown>) {
  const p = row.contractPeriod ?? row.contract_period ?? row.period ?? row.validityPeriod ?? row.validity_period
  if (p != null && String(p).trim() !== '') return String(p)
  const s = row.periodStart ?? row.period_start ?? row.contractStart ?? row.contract_start
  const e = row.periodEnd ?? row.period_end ?? row.contractEnd ?? row.contract_end
  if (s != null && e != null && String(s).trim() && String(e).trim()) return `${s} 至 ${e}`
  return '-'
}
function contractDeliveryMethod(row: Record<string, unknown>) {
  const v = row.deliveryMethod ?? row.delivery_method ?? row.deliveryType ?? row.delivery_type
  return v != null && String(v).trim() !== '' ? String(v) : '-'
}
function contractPaymentMethod(row: Record<string, unknown>) {
  const v = row.paymentMethod ?? row.payment_method ?? row.paymentType ?? row.payment_type
  return v != null && String(v).trim() !== '' ? String(v) : '-'
}

function rowBillNoForAttachments(row: Record<string, unknown>): string {
  return String(
    row.bill_no ?? row.billNo ?? row.no ?? row.documentNo ?? row.document_no ?? '',
  ).trim()
}

/**
 * 按 bill_no 将后端 Tab 返回的 `data.attachments` 合并进行数组的每一行。
 *
 * 匹配策略（由上到下）：
 *   1. attachment.bill_no 精确命中某行的 bill_no → 仅合并到该行；
 *   2. 否则（典型场景：附件源单据是 AR 应收单、被识别后路由到合同/物流/订单
 *      等其它附件表，attachment.bill_no 仍是 AR 单号，而行 bill_no 是业务单据号
 *      XSHT… / XSCKD… / XSDD…）— 视为订单级共享附件，合并到**所有行**。
 *
 * 说明：
 *   历史上 multi-row Tab 匹配不上时直接丢弃附件；single-row Tab 用 singleFallback
 *   兜底到唯一一行。这里统一兜底到所有行，保持与列表页附件状态判定的口径一致。
 */
function mergeAttachmentsByBillNo(
  rows: Record<string, unknown>[],
  attachmentRows: Record<string, unknown>[],
): Record<string, unknown>[] {
  if (attachmentRows.length === 0) return rows
  const out = rows.map((r) => ({
    ...r,
    attachments: Array.isArray(r.attachments) ? [...(r.attachments as unknown[])] : [],
  }))
  const byBill = new Map<string, Record<string, unknown>[]>()
  for (const row of out) {
    const k = rowBillNoForAttachments(row)
    if (!k) continue
    const list = byBill.get(k) ?? []
    list.push(row)
    byBill.set(k, list)
  }
  for (const row of attachmentRows) {
    const bill = String(row.bill_no ?? row.billNo ?? '').trim()
    const name = String(
      row.erp_attachment_name ??
        row.erpAttachmentName ??
        row.name ??
        row.fileName ??
        row.file_name ??
        row.attachmentName ??
        row.title ??
        '附件',
    ).trim() || '附件'
    const url = String(
      row.url ?? row.fileUrl ?? row.file_url ?? row.downloadUrl ?? row.download_url ?? row.link ?? '',
    ).trim()
    if (!url) continue
    const item = { name, url }
    const targets: Record<string, unknown>[] =
      bill && byBill.has(bill)
        ? (byBill.get(bill) as Record<string, unknown>[])
        : out // 兜底：订单级共享附件（AR 源单的合同/物流/订单类附件）
    for (const target of targets) {
      const arr = (target.attachments as Record<string, unknown>[]) ?? []
      arr.push(item)
      target.attachments = arr
    }
  }
  return out
}

function withRowKeys(rows: Record<string, unknown>[], prefix: string): Record<string, unknown>[] {
  return rows.map((row, i) => {
    const k =
      row.key ??
      row.id ??
      row.fid ??
      row.FID ??
      row.billNo ??
      row.bill_no ??
      row.billid ??
      row.documentNo ??
      row.no
    const keyStr = k != null && String(k).trim() !== '' ? String(k) : `${prefix}-${i}`
    return { ...row, key: keyStr }
  })
}

/** 金蝶 CRM 销售合同单行（蛇形字段）→ 合同信息表列字段，并保留原字段供「查看详情」 */
function looksLikeKingdeeSalesContractRow(row: Record<string, unknown>): boolean {
  return (
    row.formId === 'CRM_Contract' ||
    (typeof row.bill_no === 'string' &&
      row.bill_no.trim() !== '' &&
      (typeof row.cust_id_name === 'string' || typeof row.cust_id_fname === 'string'))
  )
}

function datePart(v: unknown): string {
  if (v == null) return ''
  const s = String(v).trim()
  if (!s) return ''
  return s.includes('T') ? s.slice(0, 10) : s.length >= 10 ? s.slice(0, 10) : s
}

function mapKingdeeSalesContractToTableRow(item: Record<string, unknown>): Record<string, unknown> {
  const no = String(item.bill_no ?? item.f_vsay_text ?? item.no ?? '').trim()
  const name = String(
    item.bill_type_id_name ??
      item.bill_type_id_fname ??
      item.material_id_name ??
      item.material_id_fname ??
      item.name ??
      '',
  ).trim()
  const amount = item.contract_amount ?? item.amount ?? item.all_amount ?? item.f_vsay_amount
  const signedRaw = item.date ?? item.create_date ?? item.approve_date
  const signedAt = datePart(signedRaw) || '-'
  const partyA = String(item.saleorgid_name ?? item.sale_org_id_name ?? item.saleorgid_fname ?? '').trim()
  const partyB = String(item.cust_id_name ?? item.cust_id_fname ?? item.cust_id_number ?? '').trim()
  const d0 = datePart(item.date ?? item.delivery_date)
  const d1 = datePart(item.close_date ?? item.delivery_date)
  let contractPeriod = '-'
  if (d0 && d1) contractPeriod = `${d0} 至 ${d1}`
  else if (d0) contractPeriod = d0

  const deliveryParts: string[] = []
  if (item.delivery_date) deliveryParts.push(`交货日 ${datePart(item.delivery_date)}`)
  if (item.stock_unit_id_name) deliveryParts.push(String(item.stock_unit_id_name))
  const entryNote = String(item.entry_note ?? '').trim()
  const deliveryMethod =
    entryNote && entryNote !== ' ' ? entryNote : deliveryParts.length > 0 ? deliveryParts.join('；') : '-'

  const paymentMethod = String(
    item.rec_condition_id_fname ?? item.rec_condition_id_name ?? item.rec_condition_id_number ?? '',
  ).trim()
  const documentNo = String(item.bill_no ?? item.src_bill_no ?? '').trim()

  return {
    ...item,
    no: no || String(item.fid ?? item._id ?? ''),
    name: name || '-',
    amount,
    signedAt,
    partyA: partyA || '-',
    partyB: partyB || '-',
    contractPeriod,
    deliveryMethod: deliveryMethod || '-',
    paymentMethod: paymentMethod || '-',
    documentNo: documentNo || undefined,
    status: formatDocumentStatus(item),
    document_status: item.document_status,
  }
}

/** 金蝶 SAL_SaleOrder 单行（蛇形字段）→ 订单信息表列字段，并保留原字段供「查看详情」 */
function looksLikeKingdeeSalesOrderRow(row: Record<string, unknown>): boolean {
  if (row.formId === 'SAL_SaleOrder') return true
  const bn = typeof row.bill_no === 'string' ? row.bill_no.trim() : ''
  if (bn && /^XSDD/i.test(bn)) return true
  if (row.bill_type_id_number === 'XSDD01_SYS') return true
  return false
}

function mapKingdeeSalesOrderToTableRow(item: Record<string, unknown>): Record<string, unknown> {
  const no = String(item.bill_no ?? item.no ?? '').trim()
  const title = String(
    item.bill_type_id_name ??
      item.bill_type_id_fname ??
      item.material_id_name ??
      item.material_id_fname ??
      item.f_vsay_text ??
      item.name ??
      '',
  ).trim()
  const amount = item.amount ?? item.all_amount ?? item.bill_all_amount
  const createdAt = datePart(item.create_date ?? item.date ?? item.approve_date) || '-'
  const documentNo = String(item.src_bill_no ?? item.bill_no ?? '').trim()

  return {
    ...item,
    no: no || String(item.fid ?? item._id ?? ''),
    title: title || '-',
    amount,
    createdAt,
    status: formatDocumentStatus(item),
    documentNo: documentNo || undefined,
    document_status: item.document_status,
  }
}

/** 金蝶 AR_RECEIVEBILL 收款单行（蛇形字段）→ 收付款信息表列字段 */
function looksLikeKingdeeReceiveBillRow(row: Record<string, unknown>): boolean {
  if (row.formId === 'AR_RECEIVEBILL') return true
  const bn = typeof row.bill_no === 'string' ? row.bill_no.trim() : ''
  return bn.length > 0 && /^SKD/i.test(bn)
}

function mapKingdeeReceiveBillToTableRow(item: Record<string, unknown>): Record<string, unknown> {
  const no = String(item.bill_no ?? item.no ?? '').trim()
  const amount = item.real_rec_amount ?? item.rec_amount ?? item.amount
  const dateRaw = item.date ?? item.create_date ?? item.payDate ?? item.modify_date
  const date = datePart(dateRaw) || '-'
  const account = String(item.accountid_name ?? item.account_id_name ?? '').trim()
  const billType = String(item.bill_type_id_fname ?? item.bill_type_id_name ?? '').trim()
  const methodParts = [billType, account].filter((x) => x && x !== ' ')
  const method = methodParts.length > 0 ? methodParts.join(' · ') : '-'
  const documentNo = String(item.src_bill_no ?? item.saleorderno ?? item.sale_order_no ?? '').trim()
  return {
    ...item,
    no: no || String(item.fid ?? item._id ?? ''),
    amount,
    date,
    method,
    status: formatDocumentStatus(item),
    documentNo: documentNo || undefined,
    document_status: item.document_status,
  }
}

/** 金蝶销售发票行 → 发票 Tab 列表列字段（兼容蛇形/驼峰） */
function mapKingdeeInvoiceToTableRow(item: Record<string, unknown>): Record<string, unknown> {
  const no = String(
    item.bill_no ?? item.invoice_no ?? item.invoiceNo ?? item.f_invoice_number ?? item.number ?? item.no ?? '',
  ).trim()
  const type = String(
    item.bill_type_id_name ??
      item.bill_type_id_fname ??
      item.invoice_type_name ??
      item.invoiceType ??
      item.type ??
      '',
  ).trim()
  const amount =
    item.f_vsay_amount ?? item.f_vsay_amount1 ?? item.amount ?? item.all_amount ?? item.tax_amount ?? item.totalAmount ?? item.total_amount
  const dateRaw =
    item.invoice_date ??
    item.invoiceDate ??
    item.biz_date ??
    item.date ??
    item.create_date ??
    item.approve_date
  const issuedAt = formatDisplayDateTime(dateRaw)
  const documentNo = String(item.src_bill_no ?? item.sale_bill_no ?? item.order_no ?? item.bill_no ?? '').trim()
  return {
    ...item,
    no: no || String(item.fid ?? item._id ?? ''),
    type: type || '-',
    amount,
    issuedAt,
    documentNo: documentNo || undefined,
  }
}

const attachmentsColumn = {
  title: '附件',
  dataIndex: '_attachments',
  key: '_attachments',
  width: 260,
  render: (_: unknown, record: Record<string, unknown>) => <AttachmentLinksCell row={record} />,
}

const AssetDataDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { message } = App.useApp()
  const [loading, setLoading] = useState(true)
  const [detail, setDetail] = useState<DetailRecord | null>(null)
  const [relationDetailOpen, setRelationDetailOpen] = useState(false)
  const [relationDetailCtx, setRelationDetailCtx] = useState<{
    tab: SupplementTab
    row: Record<string, unknown>
  } | null>(null)
  const [outstockModalOpen, setOutstockModalOpen] = useState(false)

  const [relations, setRelations] = useState<{
    contracts: Record<string, unknown>[]
    orders: Record<string, unknown>[]
    receipts: Record<string, unknown>[]
    logistics: Record<string, unknown>[]
    invoices: Record<string, unknown>[]
  }>({ contracts: [], orders: [], receipts: [], logistics: [], invoices: [] })

  const [kingdeeLoading, setKingdeeLoading] = useState(false)
  const [kingdeeError, setKingdeeError] = useState<string | null>(null)
  /** 由 `/api/data-sources/detail` 解析，供金蝶 Tab 五个接口与 `id` 一并提交 */
  const [kingdeeParams, setKingdeeParams] = useState<{
    ordernumber: string
    bill_no: string
    invoiceBillNo: string
  } | null>(null)
  const [apiContractRows, setApiContractRows] = useState<Record<string, unknown>[]>([])
  const [apiOrderRows, setApiOrderRows] = useState<Record<string, unknown>[]>([])
  const [apiReceiptRows, setApiReceiptRows] = useState<Record<string, unknown>[]>([])
  const [apiLogisticsRows, setApiLogisticsRows] = useState<Record<string, unknown>[]>([])
  const [apiInvoiceRows, setApiInvoiceRows] = useState<Record<string, unknown>[]>([])

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    setKingdeeParams(null)
    dataSourcesApi
      .detail({ id })
      .then((res: any) => {
        const payload = getDetailDataPayload(res)
        const d = pickDetail(res, id)
        setDetail(d)
        if (payload) {
          setKingdeeParams(pickKingdeeParamsFromDetail(payload))
        } else {
          setKingdeeParams(null)
        }
        const rel = payload && typeof payload === 'object' ? payload : {}
        /** 接口约定：receipts 为收款单列表；兼容旧字段 firstPayments / downPayments */
        const receiptRows = Array.isArray(rel.receipts)
          ? rel.receipts
          : Array.isArray(rel.firstPayments)
            ? rel.firstPayments
            : Array.isArray(rel.downPayments)
              ? rel.downPayments
              : []
        setRelations({
          contracts: Array.isArray(rel.contracts) ? rel.contracts : [],
          orders: Array.isArray(rel.orders) ? rel.orders : [],
          receipts: receiptRows,
          logistics: Array.isArray(rel.logistics) ? rel.logistics : [],
          invoices: Array.isArray(rel.invoices) ? rel.invoices : [],
        })
      })
      .catch(() => {
        message.error('加载详情失败')
        setDetail(null)
        setKingdeeParams(null)
      })
      .finally(() => setLoading(false))
  }, [id, message])

  useEffect(() => {
    load()
  }, [load])

  /** 金蝶 Tab：销售合同仅传 id+ordernumber；其余 Tab 另传 bill_no（发票用 invoiceBillNo 优先） */
  useEffect(() => {
    if (!id || !kingdeeParams) {
      setApiContractRows([])
      setApiOrderRows([])
      setApiReceiptRows([])
      setApiLogisticsRows([])
      setApiInvoiceRows([])
      setKingdeeLoading(false)
      return
    }

    let cancelled = false
    setKingdeeLoading(true)
    setKingdeeError(null)
    setApiContractRows([])
    setApiOrderRows([])
    setApiReceiptRows([])
    setApiLogisticsRows([])
    setApiInvoiceRows([])

    const body = {
      id,
      ordernumber: kingdeeParams.ordernumber,
      bill_no: kingdeeParams.bill_no,
    }
    /** 发票接口要求与其它 Tab 相同字段名；`bill_no` 优先用详情里的应收/发票单号（如 AR00003922） */
    const invoiceBody = {
      id,
      ordernumber: kingdeeParams.ordernumber,
      bill_no: kingdeeParams.invoiceBillNo || kingdeeParams.bill_no,
    }

    const calls = [
      dataSourcesApi.kingdeeTabSalesContract({
        id,
        ordernumber: kingdeeParams.ordernumber,
        bill_no: kingdeeParams.bill_no,
      }),
      dataSourcesApi.kingdeeTabSalesOrder(body),
      dataSourcesApi.kingdeeTabReceiveBills(body),
      dataSourcesApi.kingdeeTabSalesOutstock(body),
      dataSourcesApi.kingdeeTabSalesInvoice(invoiceBody),
    ] as const

    Promise.allSettled(calls)
      .then((results) => {
        if (cancelled) return
        const pick = (i: number) =>
          results[i].status === 'fulfilled' ? unwrapKingdeeTabRows(results[i].value) : []
        const pickAtt = (i: number) =>
          results[i].status === 'fulfilled' ? unwrapKingdeeTabAttachments(results[i].value) : []

        const rawContracts = pick(0)
        const contractsNormalized = rawContracts.map((r) =>
          looksLikeKingdeeSalesContractRow(r) ? mapKingdeeSalesContractToTableRow(r) : r,
        )
        const contractsKeyed = withRowKeys(contractsNormalized, 'kc')
        const mergedContracts = mergeAttachmentsByBillNo(contractsKeyed, pickAtt(0))
        setApiContractRows(mergedContracts)
        const rawOrders = pick(1)
        const ordersNormalized = rawOrders.map((r) =>
          looksLikeKingdeeSalesOrderRow(r) ? mapKingdeeSalesOrderToTableRow(r) : r,
        )
        setApiOrderRows(mergeAttachmentsByBillNo(withRowKeys(ordersNormalized, 'ko'), pickAtt(1)))
        const rawReceipts = pick(2)
        const receiptsNormalized = rawReceipts.map((r) =>
          looksLikeKingdeeReceiveBillRow(r) ? mapKingdeeReceiveBillToTableRow(r) : r,
        )
        setApiReceiptRows(mergeAttachmentsByBillNo(withRowKeys(receiptsNormalized, 'kr'), pickAtt(2)))
        const rawLogistics = pick(3)
        setApiLogisticsRows(mergeAttachmentsByBillNo(withRowKeys(rawLogistics, 'kl'), pickAtt(3)))
        const rawInvoices = pick(4)
        const invoicesNormalized = rawInvoices.map((r) => mapKingdeeInvoiceToTableRow(r))
        setApiInvoiceRows(mergeAttachmentsByBillNo(withRowKeys(invoicesNormalized, 'ki'), pickAtt(4)))

        const rejected = results.filter((r) => r.status === 'rejected')
        if (rejected.length === results.length) {
          setKingdeeError('金蝶关联数据加载失败，请稍后重试')
        } else if (rejected.length > 0) {
          setKingdeeError('部分金蝶关联数据加载失败，表格可能不完整')
        }
      })
      .catch(() => {
        if (!cancelled) setKingdeeError('金蝶关联数据请求异常')
      })
      .finally(() => {
        if (!cancelled) setKingdeeLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [id, kingdeeParams])

  const contractTableRows = useMemo(() => {
    if (apiContractRows.length > 0) return apiContractRows
    return withRowKeys((relations.contracts as Record<string, unknown>[]) ?? [], 'rc')
  }, [apiContractRows, relations.contracts])

  const orderTableRows = useMemo(() => {
    if (apiOrderRows.length > 0) return apiOrderRows
    return withRowKeys((relations.orders as Record<string, unknown>[]) ?? [], 'ro')
  }, [apiOrderRows, relations.orders])

  const receiptTableRows = useMemo(() => {
    if (apiReceiptRows.length > 0) return apiReceiptRows
    return withRowKeys((relations.receipts as Record<string, unknown>[]) ?? [], 'rr')
  }, [apiReceiptRows, relations.receipts])

  const logisticsTableRows = useMemo(() => {
    if (apiLogisticsRows.length > 0) return apiLogisticsRows
    return withRowKeys((relations.logistics as Record<string, unknown>[]) ?? [], 'rl')
  }, [apiLogisticsRows, relations.logistics])

  const invoiceTableRows = useMemo(() => {
    if (apiInvoiceRows.length > 0) return apiInvoiceRows
    return withRowKeys((relations.invoices as Record<string, unknown>[]) ?? [], 'ri')
  }, [apiInvoiceRows, relations.invoices])

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[240px]">
        <Spin size="large" />
      </div>
    )
  }

  const rowDetailActionColumn = (tab: SupplementTab, extra?: { outstock?: boolean }) => ({
    title: '操作',
    key: '_row_detail_op',
    width: extra?.outstock ? 180 : 100,
    fixed: 'right' as const,
    render: (_: unknown, record: Record<string, unknown>) => (
      <Space size={0} wrap>
        <Button
          type="link"
          size="small"
          onClick={() => {
            setRelationDetailCtx({ tab, row: record })
            setRelationDetailOpen(true)
          }}
        >
          查看详情
        </Button>
        {extra?.outstock ? (
          <Button type="link" size="small" onClick={() => setOutstockModalOpen(true)}>
            出库明细
          </Button>
        ) : null}
      </Space>
    ),
  })

  if (!detail) {
    return (
      <Card>
        <p className="text-gray-500 mb-4">未找到该资产数据</p>
        <Button type="primary" onClick={() => navigate('/data', { state: { restoreListCache: true } })}>
          返回列表
        </Button>
      </Card>
    )
  }

  const tabItems = [
    {
      key: 'contract',
      label: '合同',
      children: (
        <Table
          size="small"
          loading={kingdeeLoading}
          rowKey={(r) => String((r as Record<string, unknown>).key ?? (r as Record<string, unknown>).id ?? (r as Record<string, unknown>).no ?? '')}
          pagination={false}
          scroll={{ x: 2060 }}
          dataSource={contractTableRows as any[]}
          columns={[
            { title: '合同编号', dataIndex: 'no', key: 'no', width: 160, ellipsis: true },
            { title: '合同名称', dataIndex: 'name', key: 'name', width: 200, ellipsis: true },
            {
              title: '合同金额',
              dataIndex: 'amount',
              key: 'amount',
              width: 130,
              render: (_: unknown, row: Record<string, unknown>) => {
                const v = row.amount ?? row.contractAmount ?? row.contract_amount
                return fmtMoney(Number(v ?? 0))
              },
            },
            {
              title: '签订日期',
              dataIndex: 'signedAt',
              key: 'signedAt',
              width: 120,
              render: (_: unknown, row: Record<string, unknown>) =>
                String(row.signedAt ?? row.signDate ?? row.sign_date ?? row.signedDate ?? row.signed_date ?? '-') || '-',
            },
            {
              title: '结算组织',
              key: 'partyA',
              width: 200,
              ellipsis: true,
              render: (_: unknown, row: Record<string, unknown>) => contractPartyA(row),
            },
            {
              title: '客户',
              key: 'partyB',
              width: 220,
              ellipsis: true,
              render: (_: unknown, row: Record<string, unknown>) => contractPartyB(row),
            },
            {
              title: '合同期限',
              key: 'contractPeriod',
              width: 220,
              ellipsis: true,
              render: (_: unknown, row: Record<string, unknown>) => contractPeriodText(row),
            },
            {
              title: '交货方式',
              key: 'deliveryMethod',
              width: 120,
              ellipsis: true,
              render: (_: unknown, row: Record<string, unknown>) => contractDeliveryMethod(row),
            },
            {
              title: '付款方式',
              key: 'paymentMethod',
              width: 120,
              ellipsis: true,
              render: (_: unknown, row: Record<string, unknown>) => contractPaymentMethod(row),
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (_: unknown, row: Record<string, unknown>) => formatDocumentStatus(row),
            },
            attachmentsColumn,
            rowDetailActionColumn('contract', { outstock: true }),
          ]}
        />
      ),
    },
    {
      key: 'order',
      label: '订单',
      children: (
        <Table
          size="small"
          loading={kingdeeLoading}
          rowKey={(r) => String((r as Record<string, unknown>).key ?? (r as Record<string, unknown>).id ?? (r as Record<string, unknown>).no ?? '')}
          pagination={false}
          scroll={{ x: 1150 }}
          dataSource={orderTableRows as any[]}
          columns={[
            { title: '订单编号', dataIndex: 'no', key: 'no', width: 160 },
            {
              title: '订单标题',
              dataIndex: 'title',
              key: 'title',
              render: (_: unknown, row: Record<string, unknown>) =>
                String(row.title ?? row.orderTitle ?? row.order_title ?? row.subject ?? row.name ?? '-') || '-',
            },
            {
              title: '金额 (¥)',
              dataIndex: 'amount',
              key: 'amount',
              width: 120,
              render: (_: unknown, row: Record<string, unknown>) =>
                `¥${Number(row.amount ?? row.totalAmount ?? row.total_amount ?? row.taxAmount ?? 0).toLocaleString()}`,
            },
            {
              title: '创建时间',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 170,
              render: (_: unknown, row: Record<string, unknown>) =>
                formatDisplayDateTime(
                  row.createdAt ??
                    row.createTime ??
                    row.create_time ??
                    row.create_date ??
                    row.bizDate ??
                    row.biz_date ??
                    row.date,
                ),
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (_: unknown, row: Record<string, unknown>) => formatDocumentStatus(row),
            },
            attachmentsColumn,
            rowDetailActionColumn('order'),
          ]}
        />
      ),
    },
    {
      key: 'firstPayment',
      label: '收款',
      children: (
        <Table
          size="small"
          loading={kingdeeLoading}
          rowKey={(r) => String((r as Record<string, unknown>).key ?? (r as Record<string, unknown>).id ?? (r as Record<string, unknown>).no ?? '')}
          pagination={false}
          scroll={{ x: 1150 }}
          dataSource={receiptTableRows as any[]}
          columns={[
            { title: '收款单号', dataIndex: 'no', key: 'no', width: 160 },
            {
              title: '收款金额 (¥)',
              dataIndex: 'amount',
              key: 'amount',
              width: 140,
              render: (_: unknown, row: Record<string, unknown>) =>
                `¥${Number(
                  row.amount ??
                    row.real_rec_amount ??
                    row.rec_amount ??
                    row.receiptAmount ??
                    row.receipt_amount ??
                    row.totalAmount ??
                    row.total_amount ??
                    0,
                ).toLocaleString()}`,
            },
            {
              title: '支付日期',
              dataIndex: 'date',
              key: 'date',
              width: 120,
              render: (_: unknown, row: Record<string, unknown>) =>
                String(
                  row.date ??
                    row.payDate ??
                    row.pay_date ??
                    row.bizDate ??
                    row.biz_date ??
                    row.receiveDate ??
                    row.create_date ??
                    '-',
                ) || '-',
            },
            {
              title: '支付方式',
              dataIndex: 'method',
              key: 'method',
              width: 120,
              render: (_: unknown, row: Record<string, unknown>) =>
                String(
                  row.method ??
                    row.payType ??
                    row.pay_type ??
                    row.paymentMethod ??
                    row.payment_method ??
                    row.settleType ??
                    row.settle_type ??
                    row.accountid_name ??
                    row.account_id_name ??
                    row.bill_type_id_fname ??
                    row.bill_type_id_name ??
                    '-',
                ) || '-',
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (_: unknown, row: Record<string, unknown>) => formatDocumentStatus(row),
            },
            attachmentsColumn,
            rowDetailActionColumn('firstPayment'),
          ]}
        />
      ),
    },
    {
      key: 'logistics',
      label: '物流',
      children: (
        <Table
          size="small"
          loading={kingdeeLoading}
          rowKey={(r) =>
            String(
              (r as Record<string, unknown>).key ??
                (r as Record<string, unknown>)._id ??
                (r as Record<string, unknown>).business_primary_key ??
                (r as Record<string, unknown>).id ??
                (r as Record<string, unknown>).trackingNo ??
                (r as Record<string, unknown>).tracking_no ??
                (r as Record<string, unknown>).bill_no ??
                '',
            )
          }
          pagination={false}
          scroll={{ x: 1360 }}
          dataSource={logisticsTableRows as any[]}
          columns={[
            {
              title: '单据类型',
              dataIndex: 'bill_type_id_name',
              key: 'bill_type_id_name',
              width: 170,
              ellipsis: true,
              render: (_: unknown, row: Record<string, unknown>) => {
                const name = row.bill_type_id_name ?? row.bill_type_id_fname ?? row.billTypeName
                if (name != null && String(name).trim() !== '') return String(name).trim()
                if (row.formId === 'SAL_DELIVERYNOTICE') return '发货通知'
                if (row.formId === 'SAL_OUTSTOCK') return '销售出库'
                return '-'
              },
            },
            {
              title: '单号',
              dataIndex: 'bill_no',
              key: 'bill_no',
              width: 150,
              ellipsis: true,
              render: (_: unknown, row: Record<string, unknown>) =>
                String(row.bill_no ?? row.billNo ?? row.documentNo ?? row.document_no ?? '-').trim() || '-',
            },
            {
              title: '运单号',
              dataIndex: 'trackingNo',
              key: 'trackingNo',
              width: 180,
              render: (_: unknown, row: Record<string, unknown>) => {
                const t = String(
                  row.trackingNo ??
                    row.tracking_no ??
                    row.waybillNo ??
                    row.waybill_no ??
                    row.expressNo ??
                    row.express_no ??
                    row.logisticsNo ??
                    row.carriage_no ??
                    '',
                ).trim()
                return t || '-'
              },
            },
            {
              title: '收货方',
              dataIndex: 'receiver_id_name',
              key: 'receiver_id_name',
              width: 200,
              ellipsis: true,
              render: (_: unknown, row: Record<string, unknown>) =>
                String(
                  row.receiver_id_name ??
                    row.receiverIdName ??
                    row.customer_id_name ??
                    row.customerIdName ??
                    '-',
                ).trim() || '-',
            },
            {
              title: '承运商',
              dataIndex: 'carrier',
              key: 'carrier',
              width: 140,
              render: (_: unknown, row: Record<string, unknown>) =>
                String(row.carrier ?? row.carrierName ?? row.carrier_name ?? row.logisticsCompany ?? '-') || '-',
            },
            {
              title: '更新时间',
              dataIndex: 'updatedAt',
              key: 'updatedAt',
              width: 170,
              render: (_: unknown, row: Record<string, unknown>) =>
                formatDisplayDateTime(
                  row.updatedAt ??
                    row.updateTime ??
                    row.update_time ??
                    row.modifyTime ??
                    row.modify_time ??
                    row.syncedAt ??
                    row.modify_date ??
                    row.delivery_date ??
                    row.close_date ??
                    row.bizDate,
                ),
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (_: unknown, row: Record<string, unknown>) => formatDocumentStatus(row),
            },
            attachmentsColumn,
            rowDetailActionColumn('logistics'),
          ]}
        />
      ),
    },
    {
      key: 'invoice',
      label: '发票',
      children: (
        <Table
          size="small"
          loading={kingdeeLoading}
          rowKey={(r) => String((r as Record<string, unknown>).key ?? (r as Record<string, unknown>)._id ?? (r as Record<string, unknown>).id ?? (r as Record<string, unknown>).no ?? '')}
          pagination={false}
          scroll={{ x: 1180 }}
          dataSource={invoiceTableRows as any[]}
          columns={[
            { title: '发票号码', dataIndex: 'no', key: 'no', width: 180, ellipsis: true },
            {
              title: '类型',
              dataIndex: 'type',
              key: 'type',
              width: 180,
              ellipsis: true,
              render: (_: unknown, row: Record<string, unknown>) =>
                String(row.type ?? row.bill_type_id_name ?? row.bill_type_id_fname ?? '-').trim() || '-',
            },
            {
              title: '金额 (¥)',
              dataIndex: 'amount',
              key: 'amount',
              width: 130,
              render: (_: unknown, row: Record<string, unknown>) =>
                fmtMoney(
                  Number(
                    row.f_vsay_amount ??
                      row.amount ??
                      row.all_amount ??
                      row.tax_amount ??
                      row.total_amount ??
                      0,
                  ),
                ),
            },
            {
              title: '开票日期',
              dataIndex: 'issuedAt',
              key: 'issuedAt',
              width: 170,
              render: (_: unknown, row: Record<string, unknown>) =>
                formatDisplayDateTime(
                  row.issuedAt ??
                    row.invoice_date ??
                    row.invoiceDate ??
                    row.date ??
                    row.biz_date ??
                    row.create_date,
                ),
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (_: unknown, row: Record<string, unknown>) => formatDocumentStatus(row),
            },
            attachmentsColumn,
            rowDetailActionColumn('invoice'),
          ]}
        />
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-4 items-start">
        <div>
          <Space className="mb-2">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/data', { state: { restoreListCache: true } })}
            >
              返回列表
            </Button>
          </Space>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">资产数据详情</h1>
          <p className="text-gray-500 mt-1">{detail.name}</p>
        </div>
      </div>

      <Card title="概要信息" size="small">
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label="数据项ID">{detail.id}</Descriptions.Item>
          <Descriptions.Item label="数据名称">{detail.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="来源">{detail.source || '-'}</Descriptions.Item>
          <Descriptions.Item label="类型">
            <Tag color="blue">{detail.type}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="单据状态">
            {formatOnChainLabel(detail.onChain) ||
              (detail.documentStatusName?.trim()
                ? detail.documentStatusName
                : detail.documentStatus?.trim()
                  ? formatDocumentStatus({ document_status_name: '', document_status: detail.documentStatus })
                  : '-') ||
              '-'}
          </Descriptions.Item>
          <Descriptions.Item label="合同号">{detail.contractNo || '-'}</Descriptions.Item>
          <Descriptions.Item label="合同类型">{detail.contractType || '-'}</Descriptions.Item>
          <Descriptions.Item label="客户">{detail.customer || '-'}</Descriptions.Item>
          <Descriptions.Item label="结算组织">{detail.settlementOrg || '-'}</Descriptions.Item>
          <Descriptions.Item label="商品名称">{detail.productName || '-'}</Descriptions.Item>
          <Descriptions.Item label="应收净额">{fmtMoney(detail.priceTaxTotal)}</Descriptions.Item>
          <Descriptions.Item label="收款金额">{fmtMoney(detail.receiptAmount)}</Descriptions.Item>
          <Descriptions.Item label="已开票额">{fmtMoney(detail.invoicedAmount)}</Descriptions.Item>
          <Descriptions.Item label="合同创建日期">{detail.contractCreatedDate || '-'}</Descriptions.Item>
          <Descriptions.Item label="合同收款日期">{detail.contractReceiptDate || '-'}</Descriptions.Item>
          <Descriptions.Item label="价值">{fmtMoney(detail.value)}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={statusColorMap[detail.status]}>{statusTextMap[detail.status] ?? detail.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">{formatDisplayDateTime(detail.createdTime)}</Descriptions.Item>
          {/* {detail.summary?.trim() ? (
            <Descriptions.Item label="数据摘要" span={3}>
              {detail.summary}
            </Descriptions.Item>
          ) : null} */}
        </Descriptions>
      </Card>

      <Card title="关联业务信息" size="small">
        {kingdeeError ? (
          <Alert type="warning" showIcon className="mb-3" message={kingdeeError} closable onClose={() => setKingdeeError(null)} />
        ) : null}
        <Tabs items={tabItems} destroyInactiveTabPane />
      </Card>

      <RelationRowDetailModal
        open={relationDetailOpen}
        tab={relationDetailCtx?.tab ?? null}
        row={relationDetailCtx?.row ?? null}
        onClose={() => {
          setRelationDetailOpen(false)
          setRelationDetailCtx(null)
        }}
      />
      <OutstockDetailModal
        open={outstockModalOpen}
        loading={kingdeeLoading}
        rows={logisticsTableRows}
        onClose={() => setOutstockModalOpen(false)}
        onViewRow={(row) => {
          setOutstockModalOpen(false)
          setRelationDetailCtx({ tab: 'logistics', row })
          setRelationDetailOpen(true)
        }}
      />
    </div>
  )
}

export default AssetDataDetail
